"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { usePathname } from "next/navigation";
import { toast } from "sonner";

import { axios } from "~/core/axios";
import { REACT_QUERY_KEY } from "~/core/constants/common";
import type {
  BackendApiErrorResponse,
  BackendApiResponse,
} from "~/core/types/backend-api-response.type";

import { useGmailProcessingCardStore } from "~/components/GmailProcessingCard/store";
import { useInvalidateAllIntegrations } from "~/components/IntegrationV1/hooks/useFetchAllIntegrations";
import { getAutomaticalyCreateContactsAndAccountsConsent } from "~/components/IntegrationV1/utils";

import useCurrentOrganization from "../organizations/hooks/use-current-organization";
import useCurrentUser from "../organizations/hooks/use-current-user";
import { SocketEvents } from "../socket/constants";
import { useSocketConnection } from "../socket/hooks/useSocketConnection";

import {
  GMAIL_CONNECTION_STATUS,
  GMAIL_CONNECTION_TOAST_KEYS,
} from "./constants";
import type {
  GmailCalendarEventsSocketData,
  GmailConnectionSocketData,
  GoogleSyncProgressSocketData,
  UserGmailConnectionStatusResponse,
} from "./types";

type Options = {
  refetchOnWindowFocus?: boolean;
};

export const useClientGmailConnectionStatus = (options: Options = {}) => {
  const queryClient = useQueryClient();
  const setIsProcessing = useGmailProcessingCardStore(
    state => state.setIsProcessing,
  );

  const queryResponse = useQuery({
    queryKey: [REACT_QUERY_KEY.GMAIL_CONNECTION_QUERY_KEY],
    queryFn: async () => {
      try {
        const { data } = await axios.get<
          BackendApiResponse<UserGmailConnectionStatusResponse[]>
        >("/mail/gmail/connection-status");

        if (data.data) {
          data.data.forEach(item => {
            setIsProcessing(
              item.id,
              item.connection_status === GMAIL_CONNECTION_STATUS.SYNCING,
            );
          });
        }
        const isSyncing = data.data?.some(
          item => item.connection_status === GMAIL_CONNECTION_STATUS.SYNCING,
        );

        return data.data;
      } catch (error) {
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: "always",
  });

  useListenGmailConnectionStatus(data => {
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.GMAIL_CONNECTION_QUERY_KEY],
    });
    useGmailProcessingCardStore.setState({
      [data.user_gmail_id]:
        data.connection_status === GMAIL_CONNECTION_STATUS.SYNCING,
    });
  });

  return queryResponse;
};

export const useClientGmailConnectionStatusByAccount = (
  accountId: string,
  options: Options = {},
) => {
  const setIsProcessing = useGmailProcessingCardStore(
    state => state.setIsProcessing,
  );
  const queryClient = useQueryClient();

  const queryResponse = useQuery({
    queryKey: [REACT_QUERY_KEY.GMAIL_CONNECTION_QUERY_KEY, accountId],
    queryFn: async () => {
      try {
        const { data } = await axios.get<
          BackendApiResponse<UserGmailConnectionStatusResponse>
        >(`/mail/gmail/connection-status/${accountId}`);

        if (data.data) {
          setIsProcessing(
            data.data.id,
            data.data.connection_status === GMAIL_CONNECTION_STATUS.SYNCING,
          );
        }

        return data.data;
      } catch (error) {
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: "always",
  });

  useListenGmailConnectionStatus(data => {
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.GMAIL_CONNECTION_QUERY_KEY],
    });
    useGmailProcessingCardStore.setState({
      [data.user_gmail_id]:
        data.connection_status === GMAIL_CONNECTION_STATUS.SYNCING,
    });
  });

  return queryResponse;
};

export const useConnectGmail = ({
  onMutationError,
}: {
  onMutationError?: (
    error: Error,
    variables: void,
    context: unknown,
  ) => unknown;
} = {}) => {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const invalidateAllIntegrations = useInvalidateAllIntegrations();
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (id?: string | null) => {
      try {
        const params: {
          redirect_to: string;
          user_gmail_id?: string;
          allow_people_enrichment?: string;
        } = {
          redirect_to: pathname || "",
        };

        if (id) {
          params.user_gmail_id = id;
        }

        params.allow_people_enrichment =
          await getAutomaticalyCreateContactsAndAccountsConsent();

        const { data } = await axios.get<BackendApiResponse<string>>(
          `/mail/gmail/authorization-url?${new URLSearchParams(
            params,
          ).toString()}`,
        );

        return data.data;
      } catch (error) {
        return null;
      }
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEY.ALL_EMAILS_BY_USER],
        refetchType: "all",
      });
      invalidateAllIntegrations();
      const url = new URL(data as string);
      window.location.replace(url);
    },
  });

  return { isConnecting: isPending, connectGmail: mutateAsync };
};

export const useDisconnectGmail = ({
  onSuccess,
  onMutationError,
}: {
  onSuccess?: () => void;
  onMutationError?: (
    error: Error,
    variables: void,
    context: unknown,
  ) => unknown;
} = {}) => {
  const queryClient = useQueryClient();

  const invalidateAllIntegrations = useInvalidateAllIntegrations();

  const { isPending: isDisconnectPending, mutate: disconnectGmailAccount } =
    useMutation({
      mutationFn: async (id: string | null) => {
        toast.loading("Disconnecting Gmail account", {
          id: GMAIL_CONNECTION_TOAST_KEYS.DISCONNECT,
        });
        try {
          await axios.delete("/mail/gmail/disconnect", {
            data: {
              user_gmail_id: id,
            },
          });
          queryClient.invalidateQueries({
            queryKey: [REACT_QUERY_KEY.GMAIL_CONNECTION_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [REACT_QUERY_KEY.GET_CONNECTED_GMAILS],
          });
          queryClient.invalidateQueries({
            queryKey: [REACT_QUERY_KEY.ALL_EMAILS_BY_USER, 1, ""],
          });
          queryClient.invalidateQueries({
            queryKey: [REACT_QUERY_KEY.ALL_EMAILS_BY_USER],
            refetchType: "all",
          });
          queryClient.invalidateQueries({
            queryKey: [REACT_QUERY_KEY.IMMEDIATE_UPCOMING_EVENT_BY_USER],
          });
          invalidateAllIntegrations();
          onSuccess && onSuccess();
          toast.success("Gmail account disconnected", {
            id: GMAIL_CONNECTION_TOAST_KEYS.DISCONNECT,
          });
        } catch (err) {
          const error = err as AxiosError<BackendApiErrorResponse>;

          let msg = "Couldn't disconnect! Please notify the founders!";
          if (error.response) {
            msg =
              error.response.data.message !== "Internal Server Error"
                ? error.response.data.message
                : msg;
          }

          toast.error(msg, {
            id: GMAIL_CONNECTION_TOAST_KEYS.DISCONNECT,
          });
        }
      },
    });

  return { isDisconnectPending, disconnectGmailAccount };
};

export const useListenGmailConnectionStatus = (
  callback: (data: GmailConnectionSocketData) => void,
) => {
  const currentOrganization = useCurrentOrganization();

  const event = SocketEvents.gmail.connectionStatusUpdated;
  const roomName = `${event}:room:${currentOrganization?.org_id}`;

  useSocketConnection<GmailConnectionSocketData>({
    event,
    room: roomName,
    messageCallback: data => {
      callback(data.data);
    },
  });
};

export const useListenerGoogleSyncProgress = (
  callback: (data: GoogleSyncProgressSocketData) => void,
) => {
  const currentOrganization = useCurrentOrganization();
  const currentUser = useCurrentUser();
  const event = SocketEvents.gmail.syncProgress;
  const roomName = `${event}:room:${currentOrganization?.org_id}:${currentUser?.user_id}`;

  useSocketConnection<GoogleSyncProgressSocketData>({
    event,
    room: roomName,
    messageCallback: data => {
      callback(data.data);
    },
  });
};

export const useListenGmailCalendarEvents = (
  callback: (data: GmailCalendarEventsSocketData) => void,
) => {
  const currentOrganization = useCurrentOrganization();

  const event = SocketEvents.googleCalendar.calendarEventsUpdated;
  const roomName = `${event}:room:${currentOrganization?.org_id}`;

  useSocketConnection<GmailCalendarEventsSocketData>({
    event,
    room: roomName,
    messageCallback: data => callback(data.data),
  });
};
